import React, { useEffect, useState, useCallback } from "react";
import {
  Inbox,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  X,
  Eye,
} from "lucide-react";
import AdvisorLayout from "../components/layout/AdvisorLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  KeyFactsRow,
  ChangeRequestDetail,
  MODULE_META,
  OP_META,
  reqTitle,
} from "../components/advisor/ChangeRequestViews";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "applied", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const STATUS_META = {
  pending: { icon: Clock, cls: "text-amber-600 bg-amber-50 dark:bg-amber-500/10", label: "Awaiting client" },
  applied: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10", label: "Approved" },
  rejected: { icon: XCircle, cls: "text-rose-600 bg-rose-50 dark:bg-rose-500/10", label: "Rejected" },
  failed: { icon: AlertTriangle, cls: "text-rose-600 bg-rose-50 dark:bg-rose-500/10", label: "Failed" },
};

const MODULE_LABELS = {
  assets: "Assets",
  liabilities: "Liabilities",
  insurance: "Insurance",
  goals: "Goals",
  documents: "Documents",
  dashboard: "Dashboard",
};

export default function AdvisorApprovals() {
  const [filter, setFilter] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailReq, setDetailReq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/pfa/requests${qs}`);
      setRequests(data.requests || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdvisorLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-600 text-white">
            <Inbox className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
              Change Requests
            </h1>
            <p className="text-sm text-slate-500">
              Every change you propose waits here until the client approves it.
            </p>
          </div>
        </div>

        {/* FILTERS */}
        <div className="mt-5 flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition ${
                filter === f.key
                  ? "bg-emerald-500 text-white"
                  : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="py-16 text-center text-slate-400">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-slate-400">
              No {filter === "all" ? "" : filter} requests.
            </div>
          ) : (
            requests.map((r) => (
              <RequestRow key={r.id} req={r} onOpen={() => setDetailReq(r)} />
            ))
          )}
        </div>
      </div>

      {detailReq && (
        <AdvisorRequestDetailModal
          req={detailReq}
          onClose={() => setDetailReq(null)}
        />
      )}
    </AdvisorLayout>
  );
}

function AdvisorRequestDetailModal({ req, onClose }) {
  const mod = MODULE_META[req.module] || MODULE_META.dashboard;
  const op = OP_META[req.operation] || OP_META.update;
  const meta = STATUS_META[req.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <span className={`grid h-10 w-10 place-items-center rounded-2xl ${mod.cls}`}>
              <mod.icon className="h-5 w-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${op.cls}`}>
                  {op.label}
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {mod.label}
                </span>
              </div>
              <div className="text-xs text-slate-500">{reqTitle(req)}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 max-h-[60vh] overflow-y-auto space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              For <b>{req.client_name || "client"}</b> ·{" "}
              {new Date(req.created_at).toLocaleString()}
            </div>
            <span
              className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${meta.cls}`}
            >
              <StatusIcon className="h-3.5 w-3.5" />
              {meta.label}
            </span>
          </div>
          {req.status === "rejected" && req.client_note && (
            <div className="rounded-xl bg-rose-50 dark:bg-rose-500/5 border border-rose-200 dark:border-rose-500/20 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
              <span className="text-rose-400">Client's reason: </span>
              {req.client_note}
            </div>
          )}
          {req.status === "failed" && req.error_detail && (
            <div className="rounded-xl bg-rose-50 dark:bg-rose-500/5 border border-rose-200 dark:border-rose-500/20 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
              {req.error_detail}
            </div>
          )}
          <ChangeRequestDetail req={req} />
        </div>
      </div>
    </div>
  );
}

function RequestRow({ req, onOpen }) {
  const meta = STATUS_META[req.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const op = OP_META[req.operation] || OP_META.update;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 pl-6">
      <span className={`absolute left-0 inset-y-0 w-1.5 ${op.bar}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${op.cls}`}>
              {op.label}
            </span>
            <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
              {reqTitle(req)}
            </span>
            <span className="text-xs text-slate-400">
              · {MODULE_LABELS[req.module] || req.module} · {req.client_name || "client"}
            </span>
          </div>

          <KeyFactsRow req={req} />

          {req.advisor_note && (
            <div className="mt-1.5 text-xs italic text-slate-400">
              "{req.advisor_note}"
            </div>
          )}
          {req.status === "rejected" && req.client_note && (
            <div className="mt-1 text-xs text-rose-500">
              Client note: {req.client_note}
            </div>
          )}
          {req.status === "failed" && req.error_detail && (
            <div className="mt-1 text-xs text-rose-500">{req.error_detail}</div>
          )}
          <div className="mt-2 flex items-center gap-3">
            <button
              onClick={onOpen}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              <Eye className="h-3.5 w-3.5" /> View details
            </button>
            <span className="text-[11px] text-slate-400">
              {new Date(req.created_at).toLocaleString()}
            </span>
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${meta.cls}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {meta.label}
        </span>
      </div>
    </div>
  );
}
